import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useFeature, useUpsellBadge } from './useFeature'
import LockBadge from './LockBadge'
import UpgradeModal from './UpgradeModal'

type GateMode = 'wrap' | 'badge' | 'block' | 'hide'

interface FeatureGateProps {
  /** feature lookup_key */
  feature: string
  children?: ReactNode
  /**
   * wrap  (default) — render children disabled with a lock overlay; click upsells
   * badge          — render children live, append a small "Pro" badge that upsells
   * block          — replace children with a locked placeholder card
   * hide           — render nothing when unavailable (regardless of fe_visibility)
   */
  mode?: GateMode
  className?: string
  /** shown in `block` mode instead of the default copy */
  blockLabel?: ReactNode
}

/**
 * Gates a feature entry point on the current plan. All lock/hide behaviour is
 * driven by the backend features API (`available`, `fe_visibility`, `upsell_modal`)
 * — no plan logic here.
 */
export default function FeatureGate({
  feature: lookupKey,
  children,
  mode = 'wrap',
  className,
  blockLabel,
}: FeatureGateProps) {
  const feature = useFeature(lookupKey)
  const badge = useUpsellBadge(lookupKey)
  const [modalOpen, setModalOpen] = useState(false)

  if (feature.available) return <>{children}</>

  // Backend says hide it entirely — or the caller forced hide mode.
  if (mode === 'hide' || feature.feVisibility === 'hide') return null

  const upsell = () => {
    if (feature.upsellModal) setModalOpen(true)
  }

  const modal = (
    <UpgradeModal
      feature={feature}
      open={modalOpen}
      onOpenChange={setModalOpen}
    />
  )

  if (mode === 'badge') {
    return (
      <span className={cn('inline-flex items-center gap-1.5', className)}>
        {children}
        <button
          type="button"
          onClick={upsell}
          className="inline-flex"
          aria-label={`${feature.name} — ${badge === 'coming_soon' ? 'coming soon' : 'upgrade required'}`}
        >
          <LockBadge badge={badge} />
        </button>
        {modal}
      </span>
    )
  }

  if (mode === 'block') {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center',
          className,
        )}
      >
        <LockBadge badge={badge} />
        <p className="text-sm text-muted-foreground">
          {blockLabel ??
            (badge === 'coming_soon' ? (
              <>
                <strong>{feature.name}</strong> is coming soon with ProfitFlow
                Pro.
              </>
            ) : (
              <>
                <strong>{feature.name}</strong> is not included in your current
                plan.
              </>
            ))}
        </p>
        {feature.upsellModal && (
          <button
            type="button"
            onClick={upsell}
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            {badge === 'coming_soon' ? 'Get early access' : 'View plans'}
          </button>
        )}
        {modal}
      </div>
    )
  }

  // mode === 'wrap'
  return (
    <div className={cn('relative inline-block', className)}>
      <div className="pointer-events-none select-none opacity-50">{children}</div>
      <button
        type="button"
        onClick={upsell}
        aria-label={`${feature.name} — ${badge === 'coming_soon' ? 'coming soon' : 'upgrade required'}`}
        className="absolute inset-0 bg-transparent"
      >
        {/* Corner "notification dot" position, not centered/inline — those
            used to sit on top of the label text or a count pill (measured
            overlap on longer labels like "Money Paths" and on buttons that
            already have a count badge like "My Todos"). Sitting just outside
            the top-right corner never competes with either. */}
        <span className="absolute -right-1.5 -top-1.5">
          <LockBadge badge={badge} />
        </span>
      </button>
      {modal}
    </div>
  )
}
